import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { z } from "zod";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  TrendingUp,
  TrendingDown,
  Clock,
  FileSpreadsheet,
} from "lucide-react";
import type { SessionUser, Transaction } from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatMxnCents, formatPeriod, shiftPeriod } from "@/lib/format";
import { TransactionsTab } from "@/features/finance/TransactionsTab";
import { AccountsTab } from "@/features/finance/AccountsTab";
import { CategoriesTab } from "@/features/finance/CategoriesTab";
import { CalculatorTab } from "@/features/finance/CalculatorTab";
import { ProjectionsTab } from "@/features/finance/ProjectionsTab";
import { AnalysisTab } from "@/features/finance/AnalysisTab";
import { SuppliersTab } from "@/features/finance/SuppliersTab";
import { ArchiveTab } from "@/features/finance/ArchiveTab";
import { ImportTab } from "@/features/finance/ImportTab";
import { TaxesTab } from "@/features/finance/TaxesTab";
import { BurnRateGauge, utilidadPctTone } from "@/features/finance/BurnRateGauge";
import { PendingInvoicesCard } from "@/features/finance/PendingInvoicesCard";
import { ChartCard } from "@/features/finance/ChartCard";
import { exportTransactionsToExcel } from "@/features/finance/exportExcel";

const searchSchema = z.object({
  tab: z
    .enum([
      "resumen",
      "transacciones",
      "cuentas",
      "categorias",
      "calculadora",
      "proyecciones",
      "analisis",
      "proveedores",
      "archivero",
      "impuestos",
      "importar",
    ])
    .default("resumen"),
  period: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .default(defaultPeriod()),
  // Filtro pre-seleccionado de cuenta. Se setea al hacer click en "Ver
  // movimientos" desde la tarjeta de una cuenta. La pestaña Transacciones
  // lo consume como filtro inicial.
  account: z.string().optional(),
});

type Search = z.input<typeof searchSchema>;

export const Route = createFileRoute("/")({
  component: FinancePage,
  validateSearch: searchSchema,
  // El role gate vive en fin-api (accessAuth bloquea con 403 si rol no
  // es admin|finanzas). Acá no duplicamos el guard porque bloquearía el
  // render mientras /me resuelve y, si /me tarda o falla, deja el SPA en
  // loading permanente. Si el rol está mal el SPA renderiza vacío y las
  // queries devolverán 403 — UX preferible a pantalla blanca infinita.
});

interface BreakdownEntry {
  ingresos: number;
  ingresosIva: number;
  gastos: number;
  gastosIva: number;
}

interface MonthSummary {
  period: string;
  ingresosCobradosCents: number;
  gastosPagadosCents: number;
  baseIsrCents: number;
  isrCents: number;
  ivaTrasladadoCents: number;
  ivaAcreditableCents: number;
  ivaSaldoCents: number;
  byAccount: Record<string, BreakdownEntry>;
  byCategory: Record<string, BreakdownEntry & { group: string }>;
  byProject: Record<string, BreakdownEntry & { type: string | null }>;
}

function FinancePage() {
  const { tab, period, account } = Route.useSearch();

  const me = useQuery<SessionUser>({ queryKey: ["me"], queryFn: () => api.get("/me") });
  const isAuthorized = me.data?.role === "admin" || me.data?.role === "finanzas";

  if (me.data && !isAuthorized) {
    return (
      <div className="card-elevated p-8 text-center">
        <h2 className="font-display font-semibold text-lg mb-2">Sin acceso</h2>
        <p className="text-sm text-base-content/65">
          El módulo de finanzas es solo para roles <strong>admin</strong> y{" "}
          <strong>finanzas</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="finanzas RESICO PM"
        title="Flujo, ISR y IVA"
        subtitle="ISR mensual = 30% × (ingresos cobrados − gastos pagados). IVA trasladado vs acreditable."
      />

      {tab === "resumen" && <ResumenTab period={period} />}
      {tab === "transacciones" && <TransactionsTab period={period} initialAccountId={account} />}
      {tab === "cuentas" && <AccountsTab />}
      {tab === "categorias" && <CategoriesTab />}
      {tab === "calculadora" && <CalculatorTab period={period} />}
      {tab === "proyecciones" && <ProjectionsTab period={period} />}
      {tab === "analisis" && <AnalysisTab period={period} />}
      {tab === "proveedores" && <SuppliersTab period={period} />}
      {tab === "archivero" && <ArchiveTab />}
      {tab === "impuestos" && <TaxesTab />}
      {tab === "importar" && <ImportTab />}
    </div>
  );
}

// ─── Resumen ──────────────────────────────────────────────────────────────

interface AccountBalance {
  accountId: string;
  name: string;
  type: "bank" | "credit_card" | "cash" | "other";
  bankName: string | null;
  creditLimitCents: number | null;
  inflowCents: number;
  outflowCents: number;
  currentBalanceCents?: number;
  usedCents?: number;
  availableCents?: number;
}

function ResumenTab({ period }: { period: string }) {
  const navigate = Route.useNavigate();
  const setPeriod = (p: string) =>
    navigate({ search: (prev: Search) => ({ ...prev, period: p }), replace: true });

  const [asOfDay, setAsOfDay] = useState<number | null>(null);
  const { lastDay, defaultDay, asOfIso } = useMemo(() => {
    const [y, m] = period.split("-").map(Number);
    const last = new Date(y!, m!, 0).getDate();
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === y && today.getMonth() === m! - 1;
    const def = isCurrentMonth ? today.getDate() : last;
    const day = asOfDay ?? def;
    return {
      lastDay: last,
      defaultDay: def,
      asOfIso: `${period}-${String(day).padStart(2, "0")}`,
    };
  }, [period, asOfDay]);

  // Reset slider cuando cambia el periodo del header.
  useEffect(() => {
    setAsOfDay(null);
  }, [period]);

  // Toggle "expandir splits" compartido con Transacciones via localStorage.
  // Cuando ON, los breakdowns byCategory/byProject usan los splits.
  const [expandSplits, setExpandSplits] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("axorial.tx.expandSplits") === "1";
  });

  const { data, isLoading } = useQuery<MonthSummary>({
    queryKey: ["finance", "summary", period, asOfIso, expandSplits],
    queryFn: () =>
      api.get(
        `/finance/summary?period=${period}&asOf=${asOfIso}${expandSplits ? "&expandSplits=1" : ""}`,
      ),
  });

  const balances = useQuery<{ balances: AccountBalance[] }>({
    queryKey: ["finance", "balances", asOfIso],
    queryFn: () => api.get(`/finance/balances?asOf=${asOfIso}`),
  });

  const isToday = asOfDay === null || asOfDay === defaultDay;

  const [exporting, setExporting] = useState(false);
  const handleExportMonth = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const [y, m] = period.split("-").map(Number);
      const last = new Date(y!, m!, 0).getDate();
      const from = `${period}-01`;
      const to = `${period}-${String(last).padStart(2, "0")}`;
      const [txsRes, accRes, catRes] = await Promise.all([
        api.get<{
          transactions: (Transaction & { companyName: string | null })[];
        }>(`/finance/transactions?from=${from}&to=${to}&affectsTotals=1`),
        api.get<{ accounts: { id: string; name: string }[] }>("/finance/accounts"),
        api.get<{ categories: { id: string; name: string }[] }>("/finance/categories"),
      ]);
      await exportTransactionsToExcel({
        transactions: txsRes.transactions,
        period,
        accountById: new Map(accRes.accounts.map((a) => [a.id, a.name])),
        categoryById: new Map(catRes.categories.map((c) => [c.id, c.name])),
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      {/* COLUMNA IZQUIERDA: navegación temporal + estado actual del mes */}
      <div className="space-y-4">
        <div className="card-elevated p-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPeriod(shiftPeriod(period, -1))}
              className="btn btn-ghost btn-sm btn-square"
              aria-label="Mes anterior"
            >
              <ChevronLeft className="size-4" />
            </button>
            <div className="flex items-center gap-2 text-base font-medium capitalize px-2">
              <Calendar className="size-4 text-base-content/55" />
              {formatPeriod(period)}
            </div>
            <button
              type="button"
              onClick={() => setPeriod(shiftPeriod(period, 1))}
              className="btn btn-ghost btn-sm btn-square"
              aria-label="Mes siguiente"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={handleExportMonth}
            disabled={exporting}
            className="btn btn-sm btn-outline btn-primary"
            title={`Descargar Excel branded con todas las transacciones de ${formatPeriod(period)}`}
          >
            {exporting ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <FileSpreadsheet className="size-4" />
            )}
            <span className="hidden sm:inline">Excel del mes</span>
          </button>
        </div>

        <TimelineSlider
          period={period}
          day={asOfDay ?? defaultDay}
          lastDay={lastDay}
          defaultDay={defaultDay}
          isToday={isToday}
          onChange={setAsOfDay}
        />

        {isLoading || !data ? (
          <div className="grid place-items-center py-20">
            <span className="loading loading-spinner loading-lg text-primary" />
          </div>
        ) : (
          <>
            {data.ingresosCobradosCents > 0 && (
              <BurnRateCard
                ingresosCents={data.ingresosCobradosCents}
                gastosCents={data.gastosPagadosCents}
                isrCents={data.isrCents}
                ivaSaldoCents={data.ivaSaldoCents}
              />
            )}
            {balances.data && balances.data.balances.length > 0 && (
              <BalancesCard
                balances={balances.data.balances}
                asOfLabel={isToday ? null : asOfIso}
              />
            )}
            <ExpandSplitsToggle
              checked={expandSplits}
              onChange={(v) => {
                setExpandSplits(v);
                if (typeof window !== "undefined") {
                  localStorage.setItem("axorial.tx.expandSplits", v ? "1" : "0");
                }
              }}
            />
            <Breakdown title="Por categoría" data={data.byCategory} groupHeaders />
          </>
        )}
      </div>

      {/* COLUMNA DERECHA: KPIs + tendencia anual + desgloses restantes */}
      <div className="space-y-4">
        {!isLoading && data && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* El KPI muestra el subtotal (lo que cuenta para ISR), NO el
                  total con IVA. El IVA es dinero del SAT que pasa por la
                  cuenta, no es ingreso real de Axorial. El IVA queda como
                  hint debajo para referencia. La cobranza total con IVA se
                  ve en "Saldos actuales" / "Movimiento diario". */}
              <KpiCard
                label="Ingresos cobrados"
                value={data.ingresosCobradosCents}
                ivaCents={data.ivaTrasladadoCents}
                tone="success"
                icon={TrendingUp}
              />
              <KpiCard
                label="Gastos pagados"
                value={data.gastosPagadosCents}
                ivaCents={data.ivaAcreditableCents}
                tone="error"
                icon={TrendingDown}
              />
              <KpiCard
                label="ISR a pagar"
                value={data.isrCents}
                tone={data.isrCents > 0 ? "warning" : "neutral"}
                hint={`Base: ${formatMxnCents(Math.max(0, data.baseIsrCents))} × 30%`}
              />
              <KpiCard
                label="IVA saldo"
                value={data.ivaSaldoCents}
                tone={data.ivaSaldoCents > 0 ? "warning" : "success"}
                hint={
                  data.ivaSaldoCents > 0
                    ? "A cargo (a pagar al SAT)"
                    : data.ivaSaldoCents < 0
                      ? "A favor (compensable)"
                      : "Equilibrado"
                }
              />
              {(() => {
                // Utilidad bruta = ingresos − gastos (operativa, pre-impuestos).
                // Utilidad neta = bruta − ISR − IVA saldo. Saldo positivo (a
                // pagar) resta caja; negativo (a favor) suma porque no descarga.
                const utilidadBruta = data.ingresosCobradosCents - data.gastosPagadosCents;
                const utilidadNeta = utilidadBruta - data.isrCents - data.ivaSaldoCents;
                const pctBruta =
                  data.ingresosCobradosCents > 0
                    ? (utilidadBruta / data.ingresosCobradosCents) * 100
                    : 0;
                const pctNeta =
                  data.ingresosCobradosCents > 0
                    ? (utilidadNeta / data.ingresosCobradosCents) * 100
                    : 0;
                return (
                  <>
                    <KpiCard
                      label="Utilidad bruta"
                      value={utilidadBruta}
                      tone={utilidadBruta >= 0 ? "success" : "error"}
                      pctLabel={
                        data.ingresosCobradosCents > 0 ? `${pctBruta.toFixed(1)}%` : undefined
                      }
                      pctTone={utilidadPctTone(pctBruta)}
                      hint="ingresos − gastos"
                    />
                    <KpiCard
                      label="Utilidad neta"
                      value={utilidadNeta}
                      tone={utilidadNeta >= 0 ? "success" : "error"}
                      pctLabel={
                        data.ingresosCobradosCents > 0 ? `${pctNeta.toFixed(1)}%` : undefined
                      }
                      pctTone={utilidadPctTone(pctNeta)}
                      hint="bruta − ISR − IVA"
                    />
                  </>
                );
              })()}
            </div>
            <MonthTrendCard period={period} />
            <Breakdown title="Por proyecto" data={data.byProject} projectMode />
            <Breakdown title="Por cuenta" data={data.byAccount} />
            <PendingInvoicesCard
              from={`${period}-01`}
              to={`${period}-${String(
                new Date(parseInt(period.slice(0, 4)), parseInt(period.slice(5, 7)), 0).getDate(),
              ).padStart(2, "0")}`}
              queryKey="resumen"
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Gráfica de tendencia DIARIA del mes seleccionado. Ingresos vs gastos
 * (barras) y margen acumulado (línea). Suma cada tx del mes a su día
 * (solo cobrado/pagado, afecta totales, no mirrors).
 */
interface MonthTrendTx {
  date: string; // YYYY-MM-DD
  kind: "ingreso" | "gasto";
  totalCents: number;
  ivaCents: number;
}

function MonthTrendCard({ period }: { period: string }) {
  const [y, m] = period.split("-").map(Number);
  const lastDay = new Date(y!, m!, 0).getDate();
  const fromDate = `${period}-01`;
  const toDate = `${period}-${String(lastDay).padStart(2, "0")}`;
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === y && today.getMonth() === m! - 1;
  const todayDay = today.getDate();

  const txs = useQuery<{ transactions: MonthTrendTx[] }>({
    queryKey: ["finance", "month-trend", period],
    queryFn: () =>
      api.get(
        `/finance/transactions?from=${fromDate}&to=${toDate}&affectsTotals=1&paymentStatus=cobrado`,
      ),
  });
  const gastoTxs = useQuery<{ transactions: MonthTrendTx[] }>({
    queryKey: ["finance", "month-trend-gastos", period],
    queryFn: () =>
      api.get(
        `/finance/transactions?from=${fromDate}&to=${toDate}&affectsTotals=1&paymentStatus=pagado`,
      ),
  });

  if (txs.isLoading || gastoTxs.isLoading) {
    return (
      <div className="card-elevated p-5">
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner text-primary" />
        </div>
      </div>
    );
  }

  const byDay = new Map<number, { ingresos: number; gastos: number }>();
  for (let d = 1; d <= lastDay; d++) byDay.set(d, { ingresos: 0, gastos: 0 });
  for (const tx of txs.data?.transactions ?? []) {
    if (tx.kind !== "ingreso") continue;
    const day = parseInt(tx.date.slice(8, 10), 10);
    const bucket = byDay.get(day);
    if (bucket) bucket.ingresos += tx.totalCents / 100;
  }
  for (const tx of gastoTxs.data?.transactions ?? []) {
    if (tx.kind !== "gasto") continue;
    const day = parseInt(tx.date.slice(8, 10), 10);
    const bucket = byDay.get(day);
    if (bucket) bucket.gastos += tx.totalCents / 100;
  }
  let acc = 0;
  const chartData = [...byDay.entries()].map(([day, v]) => {
    acc += v.ingresos - v.gastos;
    return {
      day,
      label: String(day),
      ingresos: v.ingresos,
      gastos: v.gastos,
      acumulado: acc,
      isToday: isCurrentMonth && day === todayDay,
    };
  });

  return (
    <ChartCard
      title={`Movimiento diario · ${formatPeriod(period)}`}
      subtitle="Ingresos vs gastos por día · acumulado"
    >
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.85 0.01 280)" />
            <XAxis dataKey="label" fontSize={11} interval={lastDay > 20 ? 2 : 1} />
            <YAxis fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              formatter={(v) =>
                typeof v === "number"
                  ? v.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
                  : String(v)
              }
              labelFormatter={(label) => `Día ${label} · ${formatPeriod(period)}`}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="ingresos" name="Ingresos" fill="oklch(0.78 0.16 145)" />
            <Bar dataKey="gastos" name="Gastos" fill="oklch(0.65 0.2 25)" />
            <Line
              dataKey="acumulado"
              name="Acumulado"
              stroke="oklch(0.62 0.18 220)"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

function BurnRateCard({
  ingresosCents,
  gastosCents,
  isrCents,
  ivaSaldoCents,
}: {
  ingresosCents: number;
  gastosCents: number;
  isrCents?: number;
  ivaSaldoCents?: number;
}) {
  return (
    <ChartCard title="Salud financiera del mes">
      <BurnRateGauge
        ingresosCents={ingresosCents}
        gastosCents={gastosCents}
        isrCents={isrCents}
        ivaSaldoCents={ivaSaldoCents}
      />
    </ChartCard>
  );
}

function TimelineSlider({
  period,
  day,
  lastDay,
  defaultDay,
  isToday,
  onChange,
}: {
  period: string;
  day: number;
  lastDay: number;
  defaultDay: number;
  isToday: boolean;
  onChange: (day: number | null) => void;
}) {
  const dateLabel = formatPeriod(`${period}-${String(day).padStart(2, "0")}`);
  return (
    <div className="card-elevated p-4 space-y-2">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-base-content/55" />
          <span className="text-sm">
            <span className="text-base-content/55">Saldos y totales al </span>
            <span className="font-medium tabular-nums">
              {day} {dateLabel.split(" ")[0]} · día {day}/{lastDay}
            </span>
          </span>
        </div>
        {!isToday && (
          <button type="button" onClick={() => onChange(null)} className="btn btn-ghost btn-xs">
            Volver al día actual ({defaultDay})
          </button>
        )}
      </div>
      <input
        type="range"
        min={1}
        max={lastDay}
        value={day}
        onChange={(e) => onChange(Number(e.target.value))}
        className="range range-primary range-xs w-full"
      />
      <div className="flex justify-between text-[10px] text-base-content/45 px-1 tabular-nums">
        <span>1</span>
        <span>{Math.ceil(lastDay / 4)}</span>
        <span>{Math.ceil(lastDay / 2)}</span>
        <span>{Math.ceil((3 * lastDay) / 4)}</span>
        <span>{lastDay}</span>
      </div>
    </div>
  );
}

function BalancesCard({
  balances,
  asOfLabel,
}: {
  balances: AccountBalance[];
  asOfLabel: string | null;
}) {
  const banks = balances.filter((b) => b.type !== "credit_card");
  const cards = balances.filter((b) => b.type === "credit_card");
  const totalLiquido = banks.reduce((sum, b) => sum + (b.currentBalanceCents ?? 0), 0);
  const totalDisponibleCredito = cards.reduce((sum, b) => sum + (b.availableCents ?? 0), 0);
  const totalDeuda = cards.reduce((sum, b) => sum + (b.usedCents ?? 0), 0);

  return (
    <ChartCard
      title={asOfLabel ? `Saldos al ${asOfLabel}` : "Saldos actuales"}
      actions={
        <div className="flex items-center gap-4 text-xs">
          {banks.length > 0 && (
            <span>
              <span className="text-base-content/55">Líquido: </span>
              <span className="font-mono tabular-nums font-semibold">
                {formatMxnCents(totalLiquido)}
              </span>
            </span>
          )}
          {cards.length > 0 && (
            <>
              <span>
                <span className="text-base-content/55">Crédito disponible: </span>
                <span className="font-mono tabular-nums font-semibold text-success">
                  {formatMxnCents(totalDisponibleCredito)}
                </span>
              </span>
              <span>
                <span className="text-base-content/55">Deuda: </span>
                <span className="font-mono tabular-nums font-semibold text-error">
                  {formatMxnCents(totalDeuda)}
                </span>
              </span>
            </>
          )}
        </div>
      }
    >
      <ul className="divide-y divide-base-300/40">
        {balances.map((b) => (
          <BalanceRow key={b.accountId} balance={b} />
        ))}
      </ul>
    </ChartCard>
  );
}

function BalanceRow({ balance }: { balance: AccountBalance }) {
  const isCredit = balance.type === "credit_card";
  const value = isCredit ? (balance.availableCents ?? 0) : (balance.currentBalanceCents ?? 0);
  return (
    <li className="py-2 flex items-center gap-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate flex items-center gap-2">
          {balance.name}
          {isCredit && <span className="badge badge-xs badge-warning">TDC</span>}
          {balance.type === "cash" && <span className="badge badge-xs badge-success">Caja</span>}
        </div>
        {isCredit && balance.creditLimitCents && (
          <div className="text-xs text-base-content/55 tabular-nums">
            Deuda {formatMxnCents(balance.usedCents ?? 0)} / Límite{" "}
            {formatMxnCents(balance.creditLimitCents)}
          </div>
        )}
      </div>
      <div
        className={`font-mono tabular-nums font-semibold shrink-0 ${
          value < 0 ? "text-error" : isCredit ? "text-success" : ""
        }`}
      >
        {formatMxnCents(value)}
      </div>
    </li>
  );
}

function KpiCard({
  label,
  value,
  ivaCents,
  tone,
  icon: Icon,
  hint,
  pctLabel,
  pctTone,
}: {
  label: string;
  /** Subtotal sin IVA (lo que cuenta fiscalmente). El IVA es dinero del SAT que
   * pasa por la cuenta, no es ingreso/gasto real. Si se pasa `ivaCents`, se
   * muestra el total cobrado/pagado como referencia debajo. */
  value: number;
  ivaCents?: number;
  tone: "success" | "error" | "warning" | "neutral";
  icon?: React.ComponentType<{ className?: string }>;
  hint?: string;
  /** Texto opcional (típicamente un %) que se muestra grande a la derecha. */
  pctLabel?: string;
  /** Color independiente para el `pctLabel` (semáforo). Default = `tone`. */
  pctTone?: "success" | "error" | "warning" | "neutral";
}) {
  const colors = {
    success: "text-success",
    error: "text-error",
    warning: "text-warning",
    neutral: "text-base-content",
  };
  const hasIva = ivaCents !== undefined && ivaCents > 0;
  return (
    <div className="card-elevated p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-base-content/60">
          {label}
        </div>
        {Icon && <Icon className={`size-4 ${colors[tone]}`} />}
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-3">
        <div className={`text-2xl font-display font-bold tabular-nums ${colors[tone]}`}>
          {formatMxnCents(value)}
        </div>
        {pctLabel && (
          <div
            className={`text-2xl font-display font-bold tabular-nums shrink-0 ${colors[pctTone ?? tone]}`}
          >
            {pctLabel}
          </div>
        )}
      </div>
      {hasIva && (
        <div className="text-xs text-base-content/55 mt-1 tabular-nums">
          + IVA {formatMxnCents(ivaCents)} · Total {formatMxnCents(value + ivaCents)}
        </div>
      )}
      {hint && !hasIva && <div className="text-xs text-base-content/55 mt-1">{hint}</div>}
    </div>
  );
}

/**
 * Toggle "expandir splits" reusable. Persiste en localStorage para que la
 * preferencia sea consistente entre Resumen / Análisis / Transacciones.
 */
function ExpandSplitsToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="cursor-pointer flex items-center gap-2 text-xs text-base-content/75 px-3 py-1.5 rounded-md border border-base-300/60 bg-base-200/30">
      <input
        type="checkbox"
        className="toggle toggle-xs toggle-primary"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>Expandir sub-cuentas en desglose</span>
      <span className="text-[10px] text-base-content/45">
        (cuenta sub-categoría/proyecto cuando una tx tiene sub-cuentas)
      </span>
    </label>
  );
}

type BreakdownData = Record<string, BreakdownEntry & { group?: string; type?: string | null }>;

function Breakdown({
  title,
  data,
  groupHeaders,
  projectMode,
}: {
  title: string;
  data: BreakdownData;
  groupHeaders?: boolean;
  projectMode?: boolean;
}) {
  const entries = Object.entries(data);
  if (entries.length === 0) return null;

  if (projectMode) {
    // Sort: cliente arriba, interno medio, "Axorial (general)" al final.
    const order = (e: (typeof entries)[number]) => {
      const t = e[1].type;
      if (t === "client") return 0;
      if (t === "internal") return 1;
      return 2;
    };
    entries.sort((a, b) => order(a) - order(b));
    return (
      <ChartCard title={title}>
        <ul className="divide-y divide-base-300/40">
          {entries.map(([name, val]) => (
            <li key={name} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="truncate min-w-0 flex-1 inline-flex items-center gap-2">
                {name}
                {val.type === "client" && (
                  <span className="badge badge-xs badge-info">Cliente</span>
                )}
                {val.type === "internal" && (
                  <span className="badge badge-xs badge-ghost">Interno</span>
                )}
              </span>
              <BreakdownAmounts val={val} />
            </li>
          ))}
        </ul>
      </ChartCard>
    );
  }

  // Agrupar si tiene group
  if (groupHeaders) {
    const byGroup: Record<string, typeof entries> = {};
    for (const [name, val] of entries) {
      const g = val.group ?? "otros";
      if (!byGroup[g]) byGroup[g] = [];
      byGroup[g].push([name, val]);
    }
    return (
      <ChartCard title={title}>
        {Object.entries(byGroup).map(([group, items]) => (
          <div key={group} className="space-y-1.5">
            <div className="text-xs font-semibold capitalize text-primary">{group}</div>
            <ul className="space-y-1">
              {items.map(([name, val]) => (
                <BreakdownRow key={name} name={name} val={val} />
              ))}
            </ul>
          </div>
        ))}
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title}>
      <ul className="space-y-1">
        {entries.map(([name, val]) => (
          <BreakdownRow key={name} name={name} val={val} />
        ))}
      </ul>
    </ChartCard>
  );
}

function BreakdownRow({ name, val }: { name: string; val: BreakdownEntry }) {
  return (
    <li className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="truncate min-w-0 flex-1">{name}</span>
      <BreakdownAmounts val={val} />
    </li>
  );
}

/**
 * Muestra subtotal de ingreso/gasto (grande) e IVA aparte (pequeño).
 * IVA va en gris porque es del SAT — no es ingreso/gasto propio de Axorial.
 */
function BreakdownAmounts({ val }: { val: BreakdownEntry }) {
  return (
    <div className="flex items-center gap-3 shrink-0 text-xs font-mono tabular-nums">
      {val.ingresos > 0 && (
        <span>
          <span className="text-success">+{formatMxnCents(val.ingresos)}</span>
          {val.ingresosIva > 0 && (
            <span className="text-base-content/45 ml-1">
              · +{formatMxnCents(val.ingresosIva)} IVA
            </span>
          )}
        </span>
      )}
      {val.gastos > 0 && (
        <span>
          <span className="text-error">−{formatMxnCents(val.gastos)}</span>
          {val.gastosIva > 0 && (
            <span className="text-base-content/45 ml-1">
              · −{formatMxnCents(val.gastosIva)} IVA
            </span>
          )}
        </span>
      )}
    </div>
  );
}

function defaultPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
